import type {
  GroupUnlockCondition,
  TemplateChallengeGroup,
  TemplateChallengeLink,
} from '@/config/objects/templates';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flag, Lightbulb, Layers } from 'lucide-react';
import { useEffect, useState } from 'react';

interface GroupUnlockEditorProps {
  templateId: string;
  group: TemplateChallengeGroup;
  allGroups: TemplateChallengeGroup[];
  allChallenges: TemplateChallengeLink[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CONDITION_OPTIONS: {
  value: GroupUnlockCondition;
  label: string;
  description: string;
  icon: React.ElementType;
}[] = [
  {
    value: 'starter',
    label: 'Starter',
    description: 'Available from the start of the event',
    icon: Flag,
  },
  {
    value: 'after_challenge',
    label: 'After Challenge',
    description: 'Unlocks when a specific challenge is completed',
    icon: Lightbulb,
  },
  {
    value: 'after_group',
    label: 'After Group',
    description: 'Unlocks when all challenges in another group are completed',
    icon: Layers,
  },
];

export function GroupUnlockEditor({
  templateId,
  group,
  allGroups,
  allChallenges,
  open,
  onOpenChange,
}: GroupUnlockEditorProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const currentCondition = group.unlockConditions[0];

  const [conditionType, setConditionType] = useState<GroupUnlockCondition>('starter');
  const [triggerChallengeLinkId, setTriggerChallengeLinkId] = useState<string>('');
  const [triggerGroupId, setTriggerGroupId] = useState<string>('');

  // Filter out current group from selectable groups
  const otherGroups = allGroups.filter((g) => g.id !== group.id);

  // Reset form when dialog opens
  useEffect(() => {
    if (open && currentCondition) {
      setConditionType(currentCondition.conditionType);
      setTriggerChallengeLinkId(currentCondition.triggerChallengeLinkId || '');
      setTriggerGroupId(currentCondition.triggerGroupId || '');
    } else if (open) {
      setConditionType('starter');
      setTriggerChallengeLinkId('');
      setTriggerGroupId('');
    }
  }, [open, currentCondition]);

  const mutation = useMutation({
    mutationFn: async (data: {
      conditionType: GroupUnlockCondition;
      triggerChallengeLinkId?: string | null;
      triggerGroupId?: string | null;
    }) => {
      return apiClient.post(`/templates/${templateId}/groups/${group.id}/unlock`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TemplateGroups', templateId] });
      toast.success('Unlock condition updated');
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to update unlock condition');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: {
      conditionType: GroupUnlockCondition;
      triggerChallengeLinkId?: string | null;
      triggerGroupId?: string | null;
    } = {
      conditionType,
    };

    if (conditionType === 'after_challenge') {
      data.triggerChallengeLinkId = triggerChallengeLinkId || null;
    } else if (conditionType === 'after_group') {
      data.triggerGroupId = triggerGroupId || null;
    }

    mutation.mutate(data);
  };

  const isValid = () => {
    if (conditionType === 'starter') return true;
    if (conditionType === 'after_challenge') return !!triggerChallengeLinkId;
    if (conditionType === 'after_group') return !!triggerGroupId;
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Set Unlock Condition</DialogTitle>
            <DialogDescription>
              Configure when "{group.name}" becomes available to players
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Condition Type</Label>
              <div className="grid gap-2">
                {CONDITION_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isSelected = conditionType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setConditionType(option.value)}
                      className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Icon className={`h-5 w-5 mt-0.5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div>
                        <div className={`font-medium ${isSelected ? 'text-primary' : ''}`}>
                          {option.label}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {option.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {conditionType === 'after_challenge' && (
              <div className="space-y-2">
                <Label htmlFor="triggerChallenge">Trigger Challenge</Label>
                <Select
                  value={triggerChallengeLinkId}
                  onValueChange={setTriggerChallengeLinkId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a challenge..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allChallenges.map((link) => (
                      <SelectItem key={link.id} value={link.id}>
                        {link.libraryChallenge.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This group will unlock when the selected challenge is completed
                </p>
              </div>
            )}

            {conditionType === 'after_group' && (
              <div className="space-y-2">
                <Label htmlFor="triggerGroup">Trigger Group</Label>
                <Select value={triggerGroupId} onValueChange={setTriggerGroupId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a group..." />
                  </SelectTrigger>
                  <SelectContent>
                    {otherGroups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This group will unlock when all challenges in the selected group are completed
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid() || mutation.isPending}>
              {mutation.isPending ? 'Saving...' : 'Save Condition'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
